import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Play, ChevronRight } from "lucide-react";
import { Badge } from "../components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BeatRecommendations = ({ beatId }) => {
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (!beatId) return;
    axios.get(`${API}/beats/${beatId}/recommendations`)
      .then(res => setRecommendations(res.data))
      .catch(() => {});
  }, [beatId]);

  if (recommendations.length === 0) return null;

  return (
    <div className="mt-10" data-testid="beat-recommendations">
      <h2 className="font-heading text-2xl uppercase tracking-tight mb-6 flex items-center gap-2">
        <span className="w-1.5 h-6 bg-neon-cyan inline-block" />
        You May Also Like
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {recommendations.map((beat, index) => (
          <motion.div
            key={beat.beat_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link
              to={`/beats/${beat.beat_id}`}
              className="group block"
              data-testid={`rec-beat-${beat.beat_id}`}
            >
              <div className="relative aspect-square bg-zinc-900 border border-white/5 overflow-hidden mb-3">
                <img
                  src={beat.image_url || "https://images.unsplash.com/photo-1741746239350-9021ddfa3d59?w=400"}
                  alt={beat.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <Badge className="bg-black/60 text-neon-cyan border-0 text-xs mb-1">
                    {beat.genre}
                  </Badge>
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-12 h-12 bg-neon-cyan/20 backdrop-blur-sm flex items-center justify-center">
                    <Play className="w-6 h-6 text-neon-cyan" fill="currentColor" />
                  </div>
                </div>
              </div>
              <p className="font-medium text-sm truncate group-hover:text-neon-cyan transition-colors">
                {beat.title}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {beat.bpm} BPM  |  {beat.key}
              </p>
              <p className="text-neon-cyan font-mono text-sm font-bold mt-1">
                From ${beat.basic_price?.toFixed(2)}
              </p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export const MerchRecommendations = ({ merchId }) => {
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (!merchId) return;
    axios.get(`${API}/merch/${merchId}/recommendations`)
      .then(res => setRecommendations(res.data))
      .catch(() => {});
  }, [merchId]);

  if (recommendations.length === 0) return null;

  const categoryNames = {
    hoodies: "Hoodies", shirts: "T-Shirts", pants: "Pants", jackets: "Jackets",
    phone_cases: "Phone Cases", stickers: "Stickers", cups: "Cups & Drinkware"
  };

  return (
    <div className="mt-10" data-testid="merch-recommendations">
      <h2 className="font-heading text-2xl uppercase tracking-tight mb-6 flex items-center gap-2">
        <span className="w-1.5 h-6 bg-neon-magenta inline-block" />
        You May Also Like
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {recommendations.map((item, index) => (
          <motion.div
            key={item.merch_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link
              to={`/merch/${item.merch_id}`}
              className="group block"
              data-testid={`rec-merch-${item.merch_id}`}
            >
              <div className="relative aspect-[3/4] bg-zinc-900 border border-white/5 overflow-hidden mb-3">
                <img
                  src={item.images?.[0] || ""}
                  alt={item.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <Badge className="absolute top-3 left-3 bg-black/60 text-white border-0 text-xs uppercase">
                  {categoryNames[item.category] || item.category}
                </Badge>
              </div>
              <p className="font-medium text-sm truncate group-hover:text-neon-magenta transition-colors">
                {item.name}
              </p>
              <p className="text-neon-magenta font-mono text-sm font-bold mt-1">
                ${item.price?.toFixed(2)}
              </p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
